import { PluginConfigInterface } from '@cbsinteractive/avia-js';
import { IPauseAdProvider } from './interfaces/IPauseAdProvider';
import { IPauseAdRequest } from './interfaces/IPauseAdRequest';
import { IPauseAdResult } from './interfaces/IPauseAdResult';
import { IPauseAdsPluginApi } from './interfaces/IPauseAdsPluginApi';
import { AdImageServicePauseAdProvider } from './providers/AdImageServicePauseAdProvider';
import { FreewheelPauseAdProvider } from './providers/FreewheelPauseAdProvider';
import { GamPauseAdProvider } from './providers/GamPauseAdProvider';

/**
 * Unique identifier used to retrieve the plugin via `player.getPlugin()`.
 */
export const PAUSE_ADS_PLUGIN_ID = 'pause-ads';

/**
 * @copyright © Paramount Streaming
 * @description Avia-js plugin factory for pause ads.
 *
 * Returns a `PluginConfigInterface` that the avia player registers during
 * initialization. Once registered the plugin is accessible via:
 *
 * ```ts
 * player.getPlugin( 'pause-ads' ) // IPauseAdsPluginApi
 * ```
 *
 * Built-in providers: FreeWheel, GAM, Ad Image Service.
 * Additional providers can be registered at runtime via `registerProvider()`.
 *
 * @param options - Optional configuration forwarded from `PauseAdsPlugin.getFactoryParams()`.
 * @returns PluginConfigInterface consumed by the avia player plugin loader.
 */
export function pauseAds( options?: Record<string, unknown> ): PluginConfigInterface {
	return {
		id: PAUSE_ADS_PLUGIN_ID,
		factory: () => createPauseAdsPlugin(),
		options
	};
}

/**
 * @description Creates the internal plugin instance with provider registry and public API.
 *
 * The plugin follows a closure-based pattern (no class) to align with
 * the avia-js plugin convention used in playback-javascript-lib.
 */
function createPauseAdsPlugin(): IPauseAdsPluginApi {
	const providers = new Map<string, IPauseAdProvider>();

	// Register built-in providers
	const fwProvider = new FreewheelPauseAdProvider();
	const gamProvider = new GamPauseAdProvider();
	const aisProvider = new AdImageServicePauseAdProvider();
	providers.set( fwProvider.id, fwProvider );
	providers.set( gamProvider.id, gamProvider );
	providers.set( aisProvider.id, aisProvider );

	function getId(): string {
		return PAUSE_ADS_PLUGIN_ID;
	}

	/**
	 * @description Adds or replaces a provider in the registry.
	 * Use this to register custom providers without modifying the plugin source.
	 */
	function registerProvider( provider: IPauseAdProvider ): void {
		providers.set( provider.id, provider );
	}

	/**
	 * @description Main entry point for pause ad requests.
	 *
	 * Resolution order:
	 * 1. Look up the provider by `request.provider`.
	 * 2. Determine the URL: use `request.url` if supplied, otherwise call `provider.buildUrl()`.
	 * 3. If the provider implements `fetchPauseAd`, delegate the full fetch+parse pipeline to it.
	 *    Otherwise, fetch the VAST endpoint, parse the XML, and call `provider.parseResponse()`.
	 *
	 * Returns `{ success: false }` on any failure -- never throws.
	 */
	async function getPauseAd( request: IPauseAdRequest ): Promise<IPauseAdResult> {
		const noFill: IPauseAdResult = {
			success: false,
			imageSource: null,
			clickThrough: null,
			clickTrackingURLs: [],
			impressions: []
		};

		const provider = providers.get( request.provider );
		if ( !provider ) {
			return noFill;
		}

		// Pre-built URL takes precedence over structured params
		const safeUrl = sanitizeAdUrl( request.url || provider.buildUrl( request.params ) );
		if ( !safeUrl ) {
			return noFill;
		}

		// Non-VAST providers (e.g. JSON-based APIs) own their fetch+parse pipeline
		if ( provider.fetchPauseAd ) {
			try {
				return await provider.fetchPauseAd( safeUrl, request );
			} catch {
				return noFill;
			}
		}

		const xml = await fetchAndParseVast( safeUrl );
		if ( !xml ) {
			return noFill;
		}

		return provider.parseResponse( xml );
	}

	/**
	 * @description Tears down the plugin by clearing the provider registry.
	 */
	function destroy(): void {
		providers.clear();
	}

	return {
		getId, getPauseAd, registerProvider, destroy
	};
}

/**
 * @description Validates and sanitizes a URL for safe fetching.
 * Returns the reconstructed `URL.href` (which breaks the taint chain for
 * static analysis tools like Snyk CWE-918) or `null` if the URL is
 * malformed or not HTTPS.
 */
function sanitizeAdUrl( url: string ): string | null {
	try {
		const parsed = new URL( url );
		return parsed.protocol === 'https:' ? parsed.href : null;
	} catch {
		return null;
	}
}

/**
 * @description Fetches a VAST endpoint and returns a parsed XML `Document`, or `null` on failure.
 *
 * Applies lightweight sanitization to work around common VAST issues:
 * - Collapses whitespace/newlines that break inline XML parsing
 * - Strips empty `<Impression>` CDATA blocks that some servers return
 * - Escapes bare ampersands that are not already escaped, except inside CDATA
 *
 * @param url - Fully qualified VAST endpoint URL.
 * @returns Parsed XML Document, or `null` if the fetch or parse fails.
 */
async function fetchAndParseVast( url: string ): Promise<Document | null> {
	const safeUrl = sanitizeAdUrl( url );
	if ( !window?.fetch || !safeUrl ) {
		return null;
	}

	try {
		// deepcode ignore Ssrf: client-side fetch — URL is validated via sanitizeAdUrl (HTTPS-only, reconstructed from URL constructor); SSRF is not exploitable in a browser context
		const response = await window.fetch( safeUrl );
		let text = await response.text();

		text = text.replace( /[\r\n\t]+/g, ' ' ).trim();
		text = text.replace( /<Impression><!\[CDATA\[]><\/Impression>/g, '' );
		// Escape only bare ampersands in XML markup; CDATA tracker URLs are literal
		// and must stay intact to avoid leaking "&amp;" into fired beacons.
		text = text.replace( /<!\[CDATA\[[\s\S]*?\]\]>|&(?!amp;)/g, ( match ) => ( match === '&' ? '&amp;' : match ) );

		const xml = new DOMParser().parseFromString( text, 'text/xml' );

		if ( xml.querySelector( 'parsererror' ) ) {
			return null;
		}

		return xml;
	} catch {
		return null;
	}
}